import React from 'react';
import Box from '@mui/material/Box';

/**
 * FadeOutOverlay - Fades content at the bottom edge using CSS mask-image
 *
 * Uses mask-image instead of a colored gradient overlay, so it works with
 * any background color (including transparent/hover states).
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Content to display
 * @param {string|number} props.maxHeight - Max height when clamped (default: '90px')
 * @param {string|number} props.fadeHeight - Height of fade effect (default: '30px')
 * @param {boolean} props.enabled - Whether fade is enabled (default: true)
 * @param {boolean} props.isExpanded - When true, disables fade (default: false)
 * @param {boolean} props.clamp - When false, parent controls height (default: true)
 * @param {object} props.sx - Additional sx styles for container
 * @param {object} props.contentSx - Additional sx styles for content wrapper
 */
export default function FadeOutOverlay({
  children,
  maxHeight = '90px',
  fadeHeight = '30px',
  enabled = true,
  isExpanded = false,
  clamp = true,
  sx = {},
  contentSx = {},
}) {
  const showFade = enabled && !isExpanded;

  // Convert fadeHeight to a CSS calc-friendly value
  const fadeHeightPx =
    typeof fadeHeight === 'number' ? `${fadeHeight}px` : fadeHeight;

  // Mask gradient: fully visible until (100% - fadeHeight), then fades to transparent
  const maskGradient = `linear-gradient(to bottom, black calc(100% - ${fadeHeightPx}), transparent 100%)`;

  return (
    <Box
      sx={{
        position: 'relative',
        ...(showFade && clamp ? { maxHeight, overflow: 'hidden' } : {}),
        ...(!clamp ? { height: '100%' } : {}),
        ...(showFade && {
          WebkitMaskImage: maskGradient,
          maskImage: maskGradient,
        }),
        ...sx,
      }}
    >
      <Box sx={{ position: 'relative', ...contentSx }}>{children}</Box>
    </Box>
  );
}
